import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, LockSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, inr } from "@/lib/api";

export const ADVANCE_PERCENT = 25;

export default function BookNowDialog({ pkg, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", travel_date: "", travelers: "1" });
  const [loading, setLoading] = useState(false);

  const pax = form.travelers === "10+" ? 10 : parseInt(form.travelers || "1", 10);
  const units = pkg.price_note === "couple" ? Math.ceil(pax / 2) : pax;
  const total = pkg.price * units;
  const advance = Math.max(1, Math.round(total * ADVANCE_PERCENT / 100));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/payments/create", { package_id: pkg.id, ...form });
      const f = document.createElement("form");
      f.method = "POST";
      f.action = r.data.action;
      Object.entries(r.data.params).forEach(([k, v]) => {
        const i = document.createElement("input");
        i.type = "hidden"; i.name = k; i.value = v;
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    } catch {
      toast.error("Payment start nahi ho paya. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="booknow-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-brand-navy">Book with {ADVANCE_PERCENT}% Advance</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-brand-cream border border-brand-sand p-4 text-sm">
          <p className="font-semibold text-brand-navy line-clamp-1">{pkg.title}</p>
          <div className="mt-2 flex items-center justify-between text-[#6b6a8a]">
            <span>Price</span><span>{inr(pkg.price)} / {pkg.price_note || "person"}</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-[#6b6a8a]">
            <span>Total ({pax} {pax === 1 ? "traveller" : "travellers"}{pkg.price_note === "couple" ? ` = ${units} couple${units > 1 ? "s" : ""}` : ""})</span>
            <span className="font-semibold text-brand-navy" data-testid="booknow-total">{inr(total)}</span>
          </div>
          <div className="mt-1 flex items-center justify-between font-bold text-brand-orange text-base">
            <span>Pay now ({ADVANCE_PERCENT}% advance)</span><span data-testid="booknow-amount">{inr(advance)}</span>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="booknow-name"
            className="w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30" />
          <input required type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="booknow-email"
            className="w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30" />
          <input required value={form.phone} onChange={set("phone")} placeholder="Phone (10 digits)" pattern="[0-9+ -]{10,15}" data-testid="booknow-phone"
            className="w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30" />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" onFocus={(e) => (e.target.type = "date")} onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }} value={form.travel_date} onChange={set("travel_date")} placeholder="Travel date" data-testid="booknow-date"
              className="w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30" />
            <select required value={form.travelers} onChange={set("travelers")} data-testid="booknow-travelers"
              className="w-full rounded-xl border border-brand-sand bg-white px-4 py-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "10+"].map((n) => <option key={n} value={n}>{n} pax</option>)}
            </select>
          </div>
          <button type="submit" disabled={loading} data-testid="booknow-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            <CreditCard size={18} weight="fill" /> {loading ? "Redirecting to PayU..." : `Pay ${inr(advance)} Securely`}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#8B89A4]">
            <LockSimple size={12} weight="fill" /> Secured by PayU · UPI, Cards, NetBanking
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
